/**
 * Custom Error Handler
 * Menangani error secara global dan memfilter error yang tidak perlu di-trace
 */

import { shouldIgnoreError, shouldIgnoreUrlStatus } from "../config/signozConfig";
import { isThirdPartyError, isThirdPartyUrl } from "./thirdPartyErrorFilter";

/**
 * Safe error logger yang memfilter error sebelum di-log
 * @param {Error|string} error - Error object or message
 * @param {Object} context - Additional context
 */
export const safeErrorLog = (error, context = {}) => {
  if (shouldIgnoreError(error)) {
    // Log ke console saja, tidak ke Signoz
    console.warn("⚠️ Filtered Error:", error, context);
    return;
  }

  // Log error yang penting
  console.error("❌ Error:", error, context);
};

/**
 * Safe HTTP error logger dengan filter URL + Status Code
 * @param {Error} error - Error object
 * @param {string} url - Request URL
 * @param {number} status - HTTP status code
 * @param {Object} context - Additional context
 */
export const safeHttpErrorLog = (error, url, status, context = {}) => {
  // Check if this URL + Status combination should be ignored
  if (shouldIgnoreUrlStatus(url, status)) {
    console.warn(`⚠️ Filtered ${status} error for ${url}:`, error.message || error);
    return;
  }

  // Check if error should be ignored based on message
  if (shouldIgnoreError(error)) {
    console.warn("⚠️ Filtered Error:", error, context);
    return;
  }

  // Log error yang penting
  console.error(`❌ HTTP ${status} Error for ${url}:`, error, context);
};

/**
 * Wrapper untuk async function dengan error handling
 * @param {Function} fn - Async function to wrap
 * @param {Object} options - Options
 * @returns {Function} - Wrapped function
 */
export const withErrorHandler = (fn, options = {}) => {
  const { silent = false, fallback = null } = options;

  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (shouldIgnoreError(error)) {
        if (!silent) {
          console.warn("⚠️ Handled Error:", error.message);
        }
        return fallback;
      }

      // Re-throw error yang penting
      throw error;
    }
  };
};

/**
 * Safe fetch wrapper dengan error handling
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch response
 */
export const safeFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);

    // Check if response has error status and should be filtered
    if (!response.ok && shouldIgnoreUrlStatus(url, response.status)) {
      console.warn(`⚠️ Filtered ${response.status} response for ${url} (not traced)`);
      // Still return the response for handling, but don't trace to SigNoz
      return response;
    }

    return response;
  } catch (error) {
    if (shouldIgnoreError(error)) {
      console.warn("⚠️ Network Error (Filtered):", error.message);
      throw new Error("FILTERED_NETWORK_ERROR");
    }
    throw error;
  }
};

/**
 * Global error handler untuk window.onerror
 */
export const setupGlobalErrorHandler = () => {
  // Handle uncaught errors
  window.addEventListener("error", (event) => {
    const error = event.error || event.message;
    const filename = event.filename || "";
    const errorMessage = String(event.message || "");
    const errorStack = event.error?.stack || "";

    // Extra aggressive filtering for SignOz timeout errors
    const isSignOzTimeout = 
      (errorMessage.includes("Timeout") || errorMessage === "Timeout") &&
      (errorStack.includes("zone.js") || 
       errorStack.includes("BatchSpanProcessor") ||
       filename.includes("zone.js") ||
       filename.includes("bundle.js"));

    if (isSignOzTimeout) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // Completely suppress - don't even log
      return;
    }

    // Filter using centralized third-party filter
    if (isThirdPartyError(error, filename) || shouldIgnoreError(error)) {
      event.preventDefault();
      console.warn("⚠️ Filtered Third-Party Error:", {
        message: event.message,
        filename: filename,
        lineno: event.lineno,
        colno: event.colno,
      });
      return;
    }
  }, true); // Use capture phase

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const reasonStr = String(reason);
    const stack = reason instanceof Error ? (reason.stack || "") : "";

    // Extra aggressive filtering for SignOz timeout errors
    const isSignOzTimeout = 
      (reasonStr.includes("Timeout") || reasonStr === "Timeout") &&
      (stack.includes("zone.js") || 
       stack.includes("BatchSpanProcessor") ||
       stack.includes("OTLPTraceExporter"));

    if (isSignOzTimeout) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // Completely suppress - don't even log
      return;
    }

    // Filter using centralized third-party filter
    if (isThirdPartyError(reason) || shouldIgnoreError(reason)) {
      event.preventDefault();
      console.warn("⚠️ Filtered Promise Rejection:", reason);
      return;
    }
  }, true); // Use capture phase

  console.log("✅ Global error handler initialized with third-party filtering");
};

/**
 * Custom toast error yang memfilter error
 * @param {Function} toastFn - Toast function (e.g., toast.error)
 * @param {string} message - Error message
 * @param {Object} options - Toast options
 */
export const safeToastError = (toastFn, message, options = {}) => {
  // Selalu tampilkan toast ke user, tapi filter untuk Signoz
  if (shouldIgnoreError(message)) {
    console.warn("⚠️ User Error (Not traced):", message);
  }

  return toastFn(message, {
    id: "error-message",
    duration: 3000,
    ...options,
  });
};

/**
 * Wrapper untuk socket error handler
 * @param {Error} error - Socket error
 * @param {Function} callback - Callback function
 */
export const handleSocketError = (error, callback) => {
  const errorMessage = error?.message || error?.toString() || "Unknown socket error";

  if (shouldIgnoreError(errorMessage)) {
    console.warn("⚠️ Socket Error (Filtered):", errorMessage);
    return;
  }

  if (callback) {
    callback(error);
  }
};

/**
 * Safe console.error yang memfilter error
 */
export const safeConsoleError = (...args) => {
  const errorMessage = args.join(" ");

  if (shouldIgnoreError(errorMessage)) {
    console.warn("⚠️ Filtered:", ...args);
    return;
  }

  console.error(...args);
};

const errorHandlerUtils = {
  safeErrorLog,
  safeHttpErrorLog,
  withErrorHandler,
  safeFetch,
  setupGlobalErrorHandler,
  safeToastError,
  handleSocketError,
  safeConsoleError,
};

export default errorHandlerUtils;
